import { RecipientsPayloadProps } from '@components/admin/emails/manuals/components/drawer-recipients';
import { ManualEmail } from '@components/admin/emails/manuals/types';
import {
  EmailRecipients,
  RecurringEmail
} from '@components/admin/emails/recurring/types';
import {
  AutomaticEmailsProps,
  FormInputs,
  RecurringEmailProps
} from '../../redux/prop-types';
import {
  FilterOptionsProps,
  ResponseWithData,
  StandardResponse,
  buildQuery,
  get,
  post,
  put
} from './index';

type FilterProps = {
  filter?: FilterOptionsProps;
};

export type UserEmails = {
  email: string;
  id: string;
  name: string;
};

type UserEmailsResponse = {
  data: UserEmails[];
};

export async function getAutomaticEmails({
  filter
}: FilterProps): Promise<StandardResponse<AutomaticEmailsProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<AutomaticEmailsProps> }>
  > = get(`/api/automaticEmails/get-emails?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function getManualEmails({
  filter
}: FilterProps): Promise<StandardResponse<AutomaticEmailsProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<AutomaticEmailsProps> }>
  > = get(`/api/manualEmails/get-emails?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function toggleActiveAutomaticEmail({
  id,
  isActive
}: {
  id: string;
  isActive: boolean;
}): Promise<StandardResponse<AutomaticEmailsProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<AutomaticEmailsProps> }>
  > = put(
    `/api/automaticEmails/toggle-active/${id}?isActive=${isActive.toString()}`
  );

  return responseWithData.then(res => res.data.result);
}

export async function updateAutomaticEmail({
  id,
  data
}: {
  id: string;
  data: FormInputs;
}): Promise<StandardResponse<AutomaticEmailsProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<AutomaticEmailsProps> }>
  > = put(`/api/automaticEmails/edit-email/${id}`, data);
  return responseWithData.then(res => res.data.result);
}

export async function getUsersByEmail(search: string): Promise<unknown[]> {
  try {
    const responseWithData: Promise<ResponseWithData<UserEmailsResponse>> = get(
      `/user/get-emails?search=${search}`
    );

    const {
      data: { data }
    } = await responseWithData;
    return data;
  } catch {
    return [];
  }
}

export async function createManualEmail(
  payload: ManualEmail
): Promise<StandardResponse<AutomaticEmailsProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<AutomaticEmailsProps> }>
  > = post('/api/manualEmails/new-email', payload);
  return responseWithData.then(res => res.data.result);
}

export async function updateManualEmail({
  id,
  data
}: {
  id: string;
  data: FormInputs;
}): Promise<StandardResponse<AutomaticEmailsProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<AutomaticEmailsProps> }>
  > = put(`/api/manualEmails/edit-email/${id}`, data);
  return responseWithData.then(res => res.data.result);
}

export async function cancelManualEmail({
  id
}: {
  id: string;
}): Promise<StandardResponse<{ emailId: string }>> {
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<{ emailId: string }>>
  > = put(`/api/manualEmails/cancel-email/${id}`);
  return responseWithData.then(res => res.data);
}

export async function getRecurringEmails({
  filter
}: FilterProps): Promise<StandardResponse<RecurringEmailProps>> {
  const query: string = buildQuery(filter as FilterOptionsProps);

  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<RecurringEmailProps> }>
  > = get(`/api/recurringEmails/get-emails?${query}`);

  return responseWithData.then(res => res.data.result);
}

export async function createRecurringEmail(
  payload: RecurringEmail
): Promise<StandardResponse<RecurringEmailProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<RecurringEmailProps> }>
  > = post('/api/recurringEmails/new-email', payload);
  return responseWithData.then(res => res.data.result);
}

export async function updateRecurringEmail({
  id,
  data
}: {
  id: string;
  data: RecurringEmailProps;
}): Promise<StandardResponse<RecurringEmailProps>> {
  const responseWithData: Promise<
    ResponseWithData<{ result: StandardResponse<RecurringEmailProps> }>
  > = put(`/api/recurringEmails/edit-email/${id}`, data);
  return responseWithData.then(res => res.data.result);
}

export async function getEmailsByRecipient(
  recipients: RecipientsPayloadProps | EmailRecipients
) {
  const responseWithData: Promise<
    ResponseWithData<
      StandardResponse<{
        emails: string[] | { email: string; cc: string; bcc: string }[];
        listSize: number;
        pageNumber: number;
        pageSize: number;
      }>
    >
  > = post('/api/recurringEmails/get-emails-by-recipient', recipients);
  return responseWithData.then(res => {
    return res.data.result;
  });
}

export async function toggleActiveRecurringEmail({
  id,
  isActive
}: {
  id: string;
  isActive: boolean;
}): Promise<StandardResponse<{ emailId: string; isActive: boolean }>> {
  const responseWithData: Promise<
    ResponseWithData<StandardResponse<{ emailId: string; isActive: boolean }>>
  > = put(
    `/api/recurringEmails/toggle-active/${id}?isActive=${isActive.toString()}`
  );
  return responseWithData.then(res => res.data);
}

export type GetManualEmailRecipientsParams = {
  id: string;
  pageSize: number;
  pageNumber: number;
  search?: string;
  sort?: 'email' | 'status';
  order?: 'asc' | 'desc';
  status?: string;
};

export type GetManualEmailRecipientsResponse = {
  listSize: number;
  pageNumber: number;
  pageSize: number;
  emails: { email: string; status?: string }[];
};

export async function getManualEmailRecipients({
  id,
  pageSize,
  pageNumber,
  search,
  sort,
  order,
  status
}: GetManualEmailRecipientsParams): Promise<GetManualEmailRecipientsResponse> {
  const query: string = buildQuery({
    pageSize,
    pageNumber,
    search,
    sort,
    order,
    status
  });

  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: GetManualEmailRecipientsResponse };
    }>
  > = get(`/api/manualEmails/${id}/recipients?${query}`);

  return responseWithData.then(res => res.data.result.result);
}

export type RetryManualEmailRecipientParams = {
  id: string;
  email: string;
};

export type RetryManualEmailRecipientResponse = {
  message: string;
};

export async function retryManualEmailRecipient({
  id,
  email
}: RetryManualEmailRecipientParams): Promise<RetryManualEmailRecipientResponse> {
  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: RetryManualEmailRecipientResponse };
      message?: string;
      success?: boolean;
    }>
  > = post(`/api/manualEmails/${id}/retry-recipient`, { email });

  return responseWithData.then(res => {
    if (!res.response.ok) {
      const errorMessage =
        (res.data as unknown as { result?: { message?: string } })?.result
          ?.message ||
        res.data.message ||
        'Erro ao reenviar e-mail';
      throw new Error(errorMessage);
    }
    return res.data.result.result;
  });
}

export type RetryAllFailedEmailsParams = { id: string };

export type RetryAllFailedEmailsResponse = {
  totalSuccessed?: number;
  totalFailed?: number;
  message?: string;
  queued?: boolean;
  totalEmails?: number;
};

export async function retryAllFailedEmails({
  id
}: RetryAllFailedEmailsParams): Promise<RetryAllFailedEmailsResponse> {
  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: RetryAllFailedEmailsResponse };
      message?: string;
      success?: boolean;
    }>
  > = post(`/api/manualEmails/${id}/retry-all`, {});

  return responseWithData.then(res => {
    if (!res.response.ok) {
      const errorMessage =
        (res.data as unknown as { result?: { message?: string } })?.result
          ?.message ||
        res.data.message ||
        'Erro ao reenviar e-mails';
      throw new Error(errorMessage);
    }
    return res.data.result.result;
  });
}

export type RetryRecurringEmailRecipientParams = {
  id: string;
  email: string;
};

export type RetryRecurringEmailRecipientResponse = {
  message: string;
};

export async function retryRecurringEmailRecipient({
  id,
  email
}: RetryRecurringEmailRecipientParams): Promise<RetryRecurringEmailRecipientResponse> {
  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: RetryRecurringEmailRecipientResponse };
      message?: string;
      success?: boolean;
    }>
  > = post(`/api/recurringEmails/${id}/retry-recipient`, { email });

  return responseWithData.then(res => {
    if (!res.response.ok) {
      const errorMessage =
        (res.data as unknown as { result?: { message?: string } })?.result
          ?.message ||
        res.data.message ||
        'Erro ao reenviar e-mail';
      throw new Error(errorMessage);
    }
    return res.data.result.result;
  });
}

export type RetryAllFailedRecurringEmailsParams = { id: string };

export type RetryAllFailedRecurringEmailsResponse = {
  totalSuccessed?: number;
  totalFailed?: number;
  message?: string;
  queued?: boolean;
  totalEmails?: number;
};

export async function retryAllFailedRecurringEmails({
  id
}: RetryAllFailedRecurringEmailsParams): Promise<RetryAllFailedRecurringEmailsResponse> {
  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: RetryAllFailedRecurringEmailsResponse };
      message?: string;
      success?: boolean;
    }>
  > = post(`/api/recurringEmails/${id}/retry-all`, {});

  return responseWithData.then(res => {
    if (!res.response.ok) {
      const errorMessage =
        (res.data as unknown as { result?: { message?: string } })?.result
          ?.message ||
        res.data.message ||
        'Erro ao reenviar e-mails';
      throw new Error(errorMessage);
    }
    return res.data.result.result;
  });
}

export type GetRecurringEmailRecipientsParams = {
  id: string;
  pageSize: number;
  pageNumber: number;
  search?: string;
  sort?: 'email' | 'status';
  order?: 'asc' | 'desc';
  status?: string;
};

export type GetRecurringEmailRecipientsResponse = {
  listSize: number;
  pageNumber: number;
  pageSize: number;
  emails: { email: string; status?: string }[];
};

export async function getRecurringEmailRecipients({
  id,
  pageSize,
  pageNumber,
  search,
  sort,
  order,
  status
}: GetRecurringEmailRecipientsParams): Promise<GetRecurringEmailRecipientsResponse> {
  const query: string = buildQuery({
    pageSize,
    pageNumber,
    search,
    sort,
    order,
    status
  });

  const responseWithData: Promise<
    ResponseWithData<{
      result: { result: GetRecurringEmailRecipientsResponse };
    }>
  > = get(`/api/recurringEmails/${id}/recipients?${query}`);

  return responseWithData.then(res => res.data.result.result);
}
